/**
 * Project Selector Component
 * @implements US-014 - Project Listing & Navigation
 * @implements FS-002.3.2 - Project Navigation
 *
 * Dropdown component for the main navigation header that allows
 * switching between projects.
 */

import { useState, MouseEvent } from 'react';
import {
  Button,
  Menu,
  MenuItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Typography,
  CircularProgress,
  Box,
  Stack,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import AddIcon from '@mui/icons-material/Add';
import FolderIcon from '@mui/icons-material/Folder';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, gql } from '@apollo/client';
import {
  LifecycleStateBadge,
  ControlStateBadge,
  LifecycleState,
  ControlState,
} from '@/components/project';

/**
 * GraphQL query for fetching user's projects
 * @implements FS-002.3.1 - View All Projects
 */
const MY_PROJECTS_QUERY = gql`
  query MyProjects {
    myProjects {
      id
      title
      description
      lifecycleState
      controlState
      createdAt
    }
  }
`;

/**
 * Project type from the GraphQL response
 */
interface Project {
  id: string;
  title: string;
  description: string | null;
  lifecycleState: LifecycleState;
  controlState: ControlState;
  createdAt: string;
}

/**
 * Response type for MyProjects query
 */
interface MyProjectsResponse {
  myProjects: Project[];
}

/**
 * Props for ProjectSelector component
 */
export interface ProjectSelectorProps {
  /** Variant for styling */
  variant?: 'outlined' | 'text' | 'contained';
}

/**
 * ProjectSelector - Dropdown navigation for switching projects
 *
 * @implements US-014 - Project Listing & Navigation
 * @implements FS-002.3.2 - Project Navigation
 *
 * Features:
 * - Shows currently selected project title (or "Select Project" if none)
 * - Dropdown lists all projects with state badges
 * - Clicking a project navigates to /projects/:id
 * - Includes "Create New" option
 */
export const ProjectSelector = ({
  variant = 'outlined',
}: ProjectSelectorProps) => {
  const navigate = useNavigate();
  const { id: currentProjectId } = useParams<{ id: string }>();

  // Menu anchor state
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  // Fetch user's projects
  const { data, loading } = useQuery<MyProjectsResponse>(MY_PROJECTS_QUERY, {
    fetchPolicy: 'cache-first',
  });

  const projects = data?.myProjects ?? [];
  const currentProject = projects.find((p) => p.id === currentProjectId);

  /**
   * Handle button click to open menu
   */
  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  /**
   * Handle menu close
   */
  const handleClose = () => {
    setAnchorEl(null);
  };

  /**
   * Handle project selection
   * @implements FS-002.3.2 - Project Navigation
   */
  const handleProjectSelect = (projectId: string) => {
    handleClose();
    navigate(`/projects/${projectId}`);
  };

  /**
   * Handle create new project
   */
  const handleCreateProject = () => {
    handleClose();
    navigate('/projects/new');
  };

  /**
   * Handle view all projects
   */
  const handleViewAllProjects = () => {
    handleClose();
    navigate('/projects');
  };

  // Separate active and archived projects
  const activeProjects = projects.filter(
    (p) => p.lifecycleState !== 'ARCHIVED',
  );
  const archivedProjects = projects.filter(
    (p) => p.lifecycleState === 'ARCHIVED',
  );

  return (
    <>
      <Button
        id="project-selector-button"
        aria-controls={open ? 'project-selector-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        variant={variant}
        onClick={handleClick}
        endIcon={
          loading ? (
            <CircularProgress size={16} color="inherit" />
          ) : (
            <KeyboardArrowDownIcon />
          )
        }
        startIcon={<FolderIcon />}
        sx={{
          textTransform: 'none',
          minWidth: 180,
          justifyContent: 'space-between',
        }}
      >
        <Typography
          variant="body2"
          noWrap
          sx={{
            maxWidth: 150,
            textAlign: 'left',
          }}
        >
          {currentProject?.title ?? 'Select Project'}
        </Typography>
      </Button>

      <Menu
        id="project-selector-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'project-selector-button',
        }}
        PaperProps={{
          sx: {
            minWidth: 280,
            maxHeight: 400,
          },
        }}
      >
        {/* View All Projects */}
        <MenuItem onClick={handleViewAllProjects}>
          <ListItemText
            primary={
              <Typography variant="body2" color="primary">
                View All Projects
              </Typography>
            }
          />
        </MenuItem>

        <Divider />

        {/* Active Projects */}
        {activeProjects.length > 0 && (
          <>
            {activeProjects.map((project) => (
              <MenuItem
                key={project.id}
                onClick={() => handleProjectSelect(project.id)}
                selected={project.id === currentProjectId}
              >
                <ListItemText
                  primary={
                    <Typography variant="body2" fontWeight={500} noWrap>
                      {project.title}
                    </Typography>
                  }
                />
                <Stack direction="row" spacing={0.5} sx={{ ml: 1 }}>
                  {project.lifecycleState !== 'ACTIVE' && (
                    <LifecycleStateBadge
                      state={project.lifecycleState}
                      size="small"
                    />
                  )}
                  {project.controlState !== 'NORMAL' && (
                    <ControlStateBadge
                      state={project.controlState}
                      size="small"
                    />
                  )}
                </Stack>
              </MenuItem>
            ))}
          </>
        )}

        {/* Archived Projects */}
        {archivedProjects.length > 0 && (
          <>
            <Divider />
            <Box sx={{ px: 2, py: 0.5 }}>
              <Typography variant="caption" color="text.secondary">
                Archived
              </Typography>
            </Box>
            {archivedProjects.map((project) => (
              <MenuItem
                key={project.id}
                onClick={() => handleProjectSelect(project.id)}
                selected={project.id === currentProjectId}
                sx={{ opacity: 0.7 }}
              >
                <ListItemText
                  primary={
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {project.title}
                    </Typography>
                  }
                />
                <LifecycleStateBadge
                  state={project.lifecycleState}
                  size="small"
                />
              </MenuItem>
            ))}
          </>
        )}

        {/* Empty state */}
        {projects.length === 0 && !loading && (
          <MenuItem disabled>
            <ListItemText
              primary={
                <Typography variant="body2" color="text.secondary">
                  No projects yet
                </Typography>
              }
            />
          </MenuItem>
        )}

        <Divider />

        {/* Create New Project */}
        <MenuItem onClick={handleCreateProject}>
          <ListItemIcon>
            <AddIcon fontSize="small" color="primary" />
          </ListItemIcon>
          <ListItemText
            primary={
              <Typography variant="body2" color="primary">
                Create New Project
              </Typography>
            }
          />
        </MenuItem>
      </Menu>
    </>
  );
};
